import React, { useState, useEffect } from 'react';
import { dashboardAPI } from '../services/apiService';
import { getSettingsDashboardCardColors } from '../config/appConstants';
import { useSettings } from '../contexts/SettingsContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Badge
} from '@mui/material';
import {
  DirectionsCar,
  Schedule,
  Today,
  Person,
  PersonOff,
  DriveEta,
  Assignment,
  Message as MessageIcon,
  Repeat as RepeatIcon,
  AccountBalance as SettlementIcon,
  EventNote as FutureIcon,
  CheckCircle as CompletedIcon
} from '@mui/icons-material';

const DashboardMetrics = ({ onMetricClick, hasUnreadMessages = false }) => {
  const { settings } = useSettings();
  const DASHBOARD_CARD_COLORS = getSettingsDashboardCardColors(settings);
  const [metrics, setMetrics] = useState({
    assignedRides: [],
    openRides: [],
    ridesInProgress: [],
    completedUnreviewedRides: [],
    recurringRidesThisWeek: [],
    todaysRecurringRides: [],
    todaysRides: [],
    futureRides: [],
    activeDrivers: [],
    offlineDrivers: [],
    driversOnJob: [],
    unsettledDrivers: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the comprehensive dashboard endpoint
      const data = await dashboardAPI.getAll();

      setMetrics({
        assignedRides: data.assignedRides || [],
        openRides: data.openRides || [],
        ridesInProgress: data.ridesInProgress || [],
        completedUnreviewedRides: data.completedUnreviewedRides || [],
        recurringRidesThisWeek: data.recurringRidesThisWeek || [],
        todaysRecurringRides: data.todaysRecurringRides || [],
        todaysRides: data.todaysRides || [],
        futureRides: data.futureRides || [],
        activeDrivers: data.activeDrivers || [],
        offlineDrivers: data.offlineDrivers || [],
        driversOnJob: data.driversOnJob || [],
        unsettledDrivers: data.unsettledDrivers || []
      });
    } catch (err) {
      console.error('Failed to load dashboard metrics:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Refresh individual metric
  const refreshMetric = async (metricName, apiCall) => {
    try {
      const data = await apiCall();
      setMetrics(prev => ({
        ...prev,
        [metricName]: data || []
      }));
    } catch (err) {
      console.error(`Failed to refresh ${metricName}:`, err);
    }
  };

  const handleMetricClick = (metricType, data) => {
    onMetricClick(metricType, data);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>Loading dashboard metrics...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mb: 2 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadMetrics}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3, color: 'black' }}>
        Dashboard Overview
      </Typography>

      {/*  Row - Recurring (conditional) */}

      {/* First Row - today & Future */}
      <Grid container spacing={3}
        justifyContent="center"
        sx={{ maxWidth: 1200, mx: 'auto', mb: 4 }}>
        <Grid item xs={12} sm={6} md={6}>
          <Card
            sx={{
              cursor: 'pointer',
              '&:hover': { boxShadow: 6 },
              borderLeft: 4,
              borderLeftColor: DASHBOARD_CARD_COLORS.todaysRides,
              minHeight: 140
            }}
            onClick={() => handleMetricClick('todaysRides', metrics.todaysRides)}
          >
            <CardContent sx={{ p: 3, position: 'relative' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
                <Typography variant="h5" component="h3">
                  Today's Rides
                </Typography>
                <Chip
                  label={(metrics.todaysRides || []).length}
                  sx={{ fontSize: '1.3rem', fontWeight: 'bold', bgcolor: DASHBOARD_CARD_COLORS.todaysRides, color: 'white', height: 36, minWidth: 50 }}
                />
              </Box>
              <Typography variant="body1" color="text.secondary">
                Non-recurring rides today
              </Typography>
              <Today sx={{ position: 'absolute', bottom: 16, right: 16, opacity: 0.3, fontSize: 40 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <Card
            sx={{
              cursor: 'pointer',
              '&:hover': { boxShadow: 6 },
              borderLeft: 4,
              borderLeftColor: DASHBOARD_CARD_COLORS.futureRides,
              minHeight: 140
            }}
            onClick={() => handleMetricClick('futureRides', metrics.futureRides)}
          >
            <CardContent sx={{ p: 3, position: 'relative' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
                <Typography variant="h5" component="h3">
                  Future Rides
                </Typography>
                <Chip
                  label={(metrics.futureRides || []).length}
                  sx={{
                    fontSize: '1.3rem',
                    fontWeight: 'bold',
                    bgcolor: DASHBOARD_CARD_COLORS.futureRides,
                    color: 'white',
                    height: 36,
                    minWidth: 50
                  }}
                />
              </Box>
              <Typography variant="body1" color="text.secondary">
                Not yet shown to drivers
              </Typography>
              <FutureIcon sx={{ position: 'absolute', bottom: 16, right: 16, opacity: 0.3, fontSize: 40 }} />
            </CardContent>
          </Card>
        </Grid>

        {settings.showRecurringCallsTab && (
          <Grid container spacing={3}
            justifyContent="center"
            sx={{ maxWidth: 1200, mx: 'auto', mb: 4 }}>
            <Grid item xs={12} sm={6} md={6}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 6 },
                  borderLeft: 4,
                  borderLeftColor: DASHBOARD_CARD_COLORS.todaysRecurring,
                  minHeight: 140
                }}
                onClick={() => handleMetricClick('todaysRecurringRides', metrics.todaysRecurringRides)}
              >
                <CardContent sx={{ p: 3, position: 'relative' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
                    <Typography variant="h5" component="h3">
                      Today's Recurring
                    </Typography>
                    <Chip
                      label={(metrics.todaysRecurringRides || []).length}
                      sx={{ fontSize: '1.3rem', fontWeight: 'bold', bgcolor: DASHBOARD_CARD_COLORS.todaysRecurring, color: 'white', height: 36, minWidth: 50 }}
                    />
                  </Box>
                  <Typography variant="body1" color="text.secondary">
                    Recurring rides today
                  </Typography>
                  <RepeatIcon sx={{ position: 'absolute', bottom: 16, right: 16, opacity: 0.3, fontSize: 40 }} />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 6 },
                  borderLeft: 4,
                  borderLeftColor: DASHBOARD_CARD_COLORS.recurringThisWeek,
                  minHeight: 140
                }}
                onClick={() => handleMetricClick('recurringRidesThisWeek', metrics.recurringRidesThisWeek)}
              >
                <CardContent sx={{ p: 3, position: 'relative' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
                    <Typography variant="h5" component="h3">
                      Recurring This Week
                    </Typography>
                    <Chip
                      label={(metrics.recurringRidesThisWeek || []).length}
                      sx={{ fontSize: '1.3rem', fontWeight: 'bold', bgcolor: DASHBOARD_CARD_COLORS.recurringThisWeek, color: 'white', height: 36, minWidth: 50 }}
                    />
                  </Box>
                  <Typography variant="body1" color="text.secondary">
                    Recurring rides this week
                  </Typography>
                  <RepeatIcon sx={{ position: 'absolute', bottom: 16, right: 16, opacity: 0.3, fontSize: 40 }} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

      </Grid>

      {/* Third Row - Current Rides */}
      <Grid container spacing={3}
        justifyContent="center"
        sx={{ maxWidth: 1400, mx: 'auto', mb: 4 }}>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              '&:hover': { boxShadow: 6 },
              borderLeft: 4,
              borderLeftColor: DASHBOARD_CARD_COLORS.openRides,
              minHeight: 140
            }}
            onClick={() => handleMetricClick('openRides', metrics.openRides)}
          >
            <CardContent sx={{ p: 3, position: 'relative' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
                <Typography variant="h5" component="h3">
                  Open Rides
                </Typography>
                <Chip
                  label={(metrics.openRides || []).length}
                  sx={{ fontSize: '1.3rem', fontWeight: 'bold', bgcolor: DASHBOARD_CARD_COLORS.openRides, color: 'white', height: 36, minWidth: 50 }}
                />
              </Box>
              <Typography variant="body1" color="text.secondary">
                Rides not assigned
              </Typography>
              <Schedule sx={{ position: 'absolute', bottom: 16, right: 16, opacity: 0.3, fontSize: 40 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              '&:hover': { boxShadow: 6 },
              borderLeft: 4,
              borderLeftColor: DASHBOARD_CARD_COLORS.assignedRides,
              minHeight: 140
            }}
            onClick={() => handleMetricClick('assignedRides', metrics.assignedRides)}
          >
            <CardContent sx={{ p: 3, position: 'relative' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
                <Typography variant="h5" component="h3">
                  Assigned Rides
                </Typography>
                <Chip
                  label={(metrics.assignedRides || []).length}
                  sx={{ fontSize: '1.3rem', fontWeight: 'bold', bgcolor: DASHBOARD_CARD_COLORS.assignedRides, color: 'white', height: 36, minWidth: 50 }}
                />
              </Box>
              <Typography variant="body1" color="text.secondary">
                Assigned not started
              </Typography>
              <Assignment sx={{ position: 'absolute', bottom: 16, right: 16, opacity: 0.3, fontSize: 40 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              '&:hover': { boxShadow: 6 },
              borderLeft: 4,
              borderLeftColor: DASHBOARD_CARD_COLORS.ridesInProgress,
              minHeight: 140
            }}
            onClick={() => handleMetricClick('ridesInProgress', metrics.ridesInProgress)}
          >
            <CardContent sx={{ p: 3, position: 'relative' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
                <Typography variant="h5" component="h3">
                  Rides in Progress
                </Typography>
                <Chip
                  label={(metrics.ridesInProgress || []).length}
                  sx={{ fontSize: '1.3rem', fontWeight: 'bold', bgcolor: DASHBOARD_CARD_COLORS.ridesInProgress, color: 'white', height: 36, minWidth: 50 }}
                />
              </Box>
              <Typography variant="body1" color="text.secondary">
                Picked up, not dropped off
              </Typography>
              <DirectionsCar sx={{ position: 'absolute', bottom: 16, right: 16, opacity: 0.3, fontSize: 40 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              '&:hover': { boxShadow: 6 },
              borderLeft: 4,
              borderLeftColor: DASHBOARD_CARD_COLORS.completedRides,
              minHeight: 140
            }}
            onClick={() => handleMetricClick('completedUnreviewedRides', metrics.completedUnreviewedRides)}
          >
            <CardContent sx={{ p: 3, position: 'relative' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
                <Typography variant="h5" component="h3">
                  Completed Rides
                </Typography>
                <Chip
                  label={(metrics.completedUnreviewedRides || []).length}
                  sx={{ fontSize: '1.3rem', fontWeight: 'bold', bgcolor: DASHBOARD_CARD_COLORS.completedRides, color: 'white', height: 36, minWidth: 50 }}
                />
              </Box>
              <Typography variant="body1" color="text.secondary">
                Needs review
              </Typography>
              <CompletedIcon sx={{ position: 'absolute', bottom: 16, right: 16, opacity: 0.3, fontSize: 40 }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Forth Row - Drivers */}
      <Grid container spacing={3}
        justifyContent="center"
        sx={{ maxWidth: 1400, mx: 'auto', mb: 4 }}>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              '&:hover': { boxShadow: 6 },
              borderLeft: 4,
              borderLeftColor: DASHBOARD_CARD_COLORS.activeDrivers,
              minHeight: 140
            }}
            onClick={() => handleMetricClick('activeDrivers', metrics.activeDrivers)}
          >
            <CardContent sx={{ p: 3, position: 'relative' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h5" component="h3">
                    Active Drivers
                  </Typography>
                  {hasUnreadMessages && (
                    <Badge
                      color="error"
                      variant="dot"
                      sx={{ '& .MuiBadge-dot': { width: 10, height: 10 } }}
                    >
                      <MessageIcon color="error" fontSize="small" />
                    </Badge>
                  )}
                </Box>
                <Chip
                  label={(metrics.activeDrivers || []).length}
                  sx={{ fontSize: '1.3rem', fontWeight: 'bold', bgcolor: DASHBOARD_CARD_COLORS.activeDrivers, color: 'white', height: 36, minWidth: 50 }}
                />
              </Box>
              <Typography variant="body1" color="text.secondary">
                Currently online
              </Typography>
              <Person sx={{ position: 'absolute', bottom: 16, right: 16, opacity: 0.3, fontSize: 40 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              '&:hover': { boxShadow: 6 },
              borderLeft: 4,
              borderLeftColor: DASHBOARD_CARD_COLORS.driversOnJob,
              minHeight: 140
            }}
            onClick={() => handleMetricClick('driversOnJob', metrics.driversOnJob)}
          >
            <CardContent sx={{ p: 3, position: 'relative' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
                <Typography variant="h5" component="h3">
                  Drivers On Job
                </Typography>
                <Chip
                  label={(metrics.driversOnJob || []).length}
                  sx={{
                    fontSize: '1.3rem',
                    fontWeight: 'bold',
                    bgcolor: DASHBOARD_CARD_COLORS.driversOnJob,
                    color: 'white',
                    height: 36,
                    minWidth: 50
                  }}
                />
              </Box>
              <Typography variant="body1" color="text.secondary">
                Currently on active calls
              </Typography>
              <DriveEta sx={{ position: 'absolute', bottom: 16, right: 16, opacity: 0.3, fontSize: 40 }} />
            </CardContent>
          </Card>
        </Grid>

        {settings.showReportsTab && (
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                cursor: 'pointer',
                '&:hover': { boxShadow: 6 },
                borderLeft: 4,
                borderLeftColor: DASHBOARD_CARD_COLORS.unsettledDrivers,
                minHeight: 140
              }}
              onClick={() => handleMetricClick('unsettledDrivers', metrics.unsettledDrivers)}
            >
              <CardContent sx={{ p: 3, position: 'relative' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
                  <Typography variant="h5" component="h3">
                    Unsettled Drivers
                  </Typography>
                  <Chip
                    label={(metrics.unsettledDrivers || []).length}
                    sx={{
                      fontSize: '1.3rem',
                      fontWeight: 'bold',
                      bgcolor: DASHBOARD_CARD_COLORS.unsettledDrivers,
                      color: 'white',
                      height: 36,
                      minWidth: 50
                    }}
                  />
                </Box>
                <Typography variant="body1" color="text.secondary">
                  Need settlement
                </Typography>
                <SettlementIcon sx={{ position: 'absolute', bottom: 16, right: 16, opacity: 0.3, fontSize: 40 }} />
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: 'pointer',
              '&:hover': { boxShadow: 6 },
              borderLeft: 4,
              borderLeftColor: DASHBOARD_CARD_COLORS.offlineDrivers,
              minHeight: 140
            }}
            onClick={() => handleMetricClick('offlineDrivers', metrics.offlineDrivers)}
          >
            <CardContent sx={{ p: 3, position: 'relative' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
                <Typography variant="h5" component="h3">
                  Offline Drivers
                </Typography>
                <Chip
                  label={(metrics.offlineDrivers || []).length}
                  sx={{
                    fontSize: '1.3rem',
                    fontWeight: 'bold',
                    bgcolor: DASHBOARD_CARD_COLORS.offlineDrivers,
                    color: 'white',
                    height: 36,
                    minWidth: 50
                  }}
                />
              </Box>
              <Typography variant="body1" color="text.secondary">
                Not currently online
              </Typography>
              <PersonOff sx={{ position: 'absolute', bottom: 16, right: 16, opacity: 0.3, fontSize: 40 }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="center" mt={4}>
        <Button
          onClick={loadMetrics}
          variant="contained"
          color="primary"
          size="large"
        >
          Refresh Metrics
        </Button>
      </Box>
    </Box>
  );
};

export default DashboardMetrics;
